// In-dialog preview panel that mirrors the exported PDF layout. Read-only — partners edit
// in the SnapshotDialog form; this panel just re-renders what they'll get.
import { useMemo } from "react";

export default function SnapshotPreview({ draft, firmName = "Yadid Advisory", clientName }) {
  // Memoize the filtered sections + their filtered highlights so a preview re-render
  // (title/subtitle edits) doesn't allocate fresh arrays for every already-empty row.
  // NB: draft must be treated as immutable — SnapshotDialog always produces a new draft
  // object on edit. In-place mutation would leave this memo stale.
  const sections = useMemo(() => {
    if (!draft) return [];
    return (draft.sections || [])
      .filter((s) => (s.summary || (s.highlights || []).some(Boolean)))
      .map((s) => ({ ...s, _highlights: (s.highlights || []).filter(Boolean) }));
  }, [draft]);

  if (!draft) return null;
  return (
    <div className="bg-white text-[#1B1E28] border border-[#e5e5e5] shadow-sm mx-auto" style={{ maxWidth: 520, fontFamily: "-apple-system, Segoe UI, Roboto, sans-serif" }} data-testid="snapshot-preview">
      <div className="px-10 pt-8 pb-4 border-b-2 border-[#B08048] flex items-start justify-between">
        <div>
          <div className="text-base font-bold tracking-tight">{firmName}</div>
          <div className="text-[0.625rem] uppercase tracking-[0.14em] text-[#7A7F8C] mt-1">Playbook snapshot</div>
        </div>
        <div className="text-right text-[0.625rem] text-[#7A7F8C]">
          <div>Prepared for</div>
          <div className="text-[#1B1E28] font-medium">{clientName || "Client"}</div>
        </div>
      </div>

      <div className="px-10 pt-6 pb-8">
        <div className="text-xl font-bold leading-tight mb-1">{draft.title || "Progress snapshot"}</div>
        <div className="text-sm text-[#7A7F8C] mb-6">{draft.subtitle || "A reviewed summary of the arc of work delivered."}</div>

        {sections.length === 0 && (
          <div className="text-xs italic text-[#7A7F8C]">No sections approved yet — add a summary or highlight to any section to see it here.</div>
        )}

        {sections.map((s) => (
          <div key={s.label || s._highlights.join("|")} className="mb-5">
            <div className="text-[0.75rem] font-bold tracking-tight mb-1.5">{s.label}</div>
            {s.summary && <div className="text-[0.6875rem] leading-relaxed mb-1.5">{s.summary}</div>}
            {s._highlights.map((h) => (
              <div key={h} className="text-[0.6875rem] leading-relaxed pl-3">• {h}</div>
            ))}
          </div>
        ))}

        <div className="mt-6 pt-4 border-t border-[#e5e5e5]">
          <div className="text-[0.5625rem] text-[#7A7F8C] leading-relaxed">
            Prepared by {firmName} · Reviewed and approved for {clientName || "Partner Organization"}. This document summarises the arc of the engagement; internal deliberations, hypotheses, and working notes are intentionally not included.
          </div>
        </div>
      </div>
    </div>
  );
}
